import type { LifeEvent, LifeState } from './life.ts';
import type { OutingSpot } from './dating.ts';
import { Rng } from './rng.ts';

/**
 * Hanging out later: a plan with a friend for a time and a place (tonight, tomorrow, Saturday), alongside hanging out
 * right now. On the day it's in Contacts with a Go; it feeds the reminders and the "you'd miss it" warnings; a plan
 * you don't turn up to is missed (and they notice).
 */
export interface Meetup { name: string; at: number; spot: OutingSpot; place: string; state: 'planned' | 'done' | 'missed' }

export const meetupsOf = (s: LifeState): Meetup[] => (s.meetups ??= []);
export const meetupsAhead = (s: LifeState) => meetupsOf(s).filter((m) => m.state === 'planned' && m.at + 90 >= s.minutes).sort((a, b) => a.at - b.at);
/** one you can go to now: from half an hour before till an hour and a half after */
export const meetupNow = (s: LifeState): Meetup | null => meetupsOf(s).find((m) => m.state === 'planned' && s.minutes >= m.at - 30 && s.minutes <= m.at + 90) ?? null;

/** Ask: a friend says yes unless you hardly know each other (then: maybe another day). */
export function planMeetup(s: LifeState, name: string, at: number, spot: OutingSpot, place: string): { ok: boolean; line: string; events: LifeEvent[] } {
  const c = s.contacts.find((x) => x.name === name), first = name.split(' ')[0];
  if (!c) return { ok: false, line: '', events: [] };
  if (meetupsOf(s).some((m) => m.state === 'planned' && Math.abs(m.at - at) < 120)) return { ok: false, line: 'You already have plans around then.', events: [] };
  const r = new Rng((s.seed ^ Math.imul(at + name.length, 0x27d4eb2d)) >>> 0);
  if (r.next() > 0.45 + c.closeness / 100) return { ok: false, line: `${first}: "I can't that day, sorry. Another time?"`, events: [] };
  meetupsOf(s).push({ name, at, spot, place, state: 'planned' });
  return { ok: true, line: `${first}: "${place.split(',')[0]}? Yes! See you there."`, events: [{ t: s.minutes, kind: 'info', text: `Plans with ${first}.` }] };
}

export function meetupGone(m: Meetup) { m.state = 'done'; }

/** Each day: plans that came and went without you are missed, and they cool a little. */
export function meetupsDay(s: LifeState, events: LifeEvent[]) {
  for (const m of meetupsOf(s)) if (m.state === 'planned' && s.minutes > m.at + 90) {
    m.state = 'missed';
    const c = s.contacts.find((x) => x.name === m.name);
    if (c) c.closeness = Math.max(0, c.closeness - 4);
    events.push({ t: s.minutes, kind: 'info', text: `You didn't turn up to meet ${m.name.split(' ')[0]}.` });
  }
}
